import { getCodec } from '@heidi/capture/getCodec';
import { getFormat } from '@heidi/capture/getFormat';
import { DeviceInUseError } from '@heidi/capture/types';
import { useAudioGraphStore } from '@heidi/capture/useAudioGraphStore';
import { ChunkRecorder } from '@heidi/capture/useSessionRecordingStore/lib/ChunkRecorder';
import { useSessionRecordingStore } from '@heidi/capture/useSessionRecordingStore/useSessionRecordingStore';
import { dictationChunkDurations } from '@heidi/capture/utils';
import { readDefaultDictationInputLanguages } from '@heidi/workspace/data';
import { AxiosError } from 'axios';
import { get as getProp } from 'lodash-es';
import pRetry from 'p-retry';
import { type StateCreator } from 'zustand';
import { getDictationBubblePorts } from '../bubble-ports';
import { describeTranscriptionFailure } from './describeTranscriptionFailure';
import { dictationLogger } from './dictationLogger';
import { transcribeChunk } from './transcribeChunk';
import { type DictationStore, type RecordingSlice } from './types';

const { getRecorderStream, releaseStream } = useAudioGraphStore.getState();

const initialState: {
  recordingState: 'READY';
  numChunks: number;
  numChunksProcessing: number;
  totalDuration: number;
  chunkRecorder: null;
  recorderStreamId: null;
  transcribedChunks: Record<number, string>;
  hasReceivedTranscript: boolean;
  pendingLastChunkEnabled: boolean;
  pendingLastChunk: null;
} = {
  recordingState: 'READY',
  numChunks: 0,
  numChunksProcessing: 0,
  totalDuration: 0,
  chunkRecorder: null,
  recorderStreamId: null,
  transcribedChunks: {},
  hasReceivedTranscript: false,
  pendingLastChunkEnabled: false,
  pendingLastChunk: null,
};

export const createRecordingSlice: StateCreator<
  DictationStore,
  [],
  [],
  RecordingSlice
> = (set, get) => ({
  ...initialState,

  startRecording: async () => {
    const { setErrorCode } = get();
    // SCR-4118: dictation slice's `start()` mints these and writes them to
    // the store before reaching here, so logs fired during mic-permission
    // failures already carry them. Defensive fallback for any direct caller
    // that skips dictation slice's `start()` — write via `set` (intra-store)
    // rather than `setDictationIds` so this slice can be tested in isolation
    // without mocking the dictation slice.
    const existingIds = get();
    const dictationId = existingIds.dictationId ?? crypto.randomUUID();
    const dictationRecordingId = existingIds.dictationRecordingId ?? crypto.randomUUID();
    if (!existingIds.dictationId || !existingIds.dictationRecordingId) {
      set({ dictationId, dictationRecordingId });
    }
    const useLogsBackend = getDictationBubblePorts().isDictationLogsBackendEnabled();
    const audioGraphStore = useAudioGraphStore.getState();

    // Ensure AudioContext is running (browsers auto-suspend until user gesture)
    if (audioGraphStore.audioContext?.state === 'suspended') {
      await audioGraphStore.audioContext.resume();
    }

    // Starting dictation is a deliberate new acquisition attempt. A previous
    // failure leaves DEVICE_IN_USE in the graph, and rebuildGraph() refuses to
    // run while any error is present. Clear the stale marker first; the real
    // acquisition below re-adds it if the microphone is still unavailable.
    audioGraphStore.clearError('DEVICE_IN_USE');
    const { analyzer: micAnalyzer, id: micAnalyzerId } = audioGraphStore.getMicAnalyzer();
    // getMicAnalyzer fires void rebuildGraphTask() internally —
    // wait for it to complete so mic source is connected before recording.
    await audioGraphStore.enqueueTask('AWAIT_GRAPH_READY', () => {});
    const latestAudioGraph = useAudioGraphStore.getState();
    if (latestAudioGraph.errors.includes('DEVICE_IN_USE')) {
      audioGraphStore.releaseMicAnalyzer(micAnalyzerId);
      throw new DeviceInUseError('microphone device in use');
    }
    if (!latestAudioGraph.micSourceStream) {
      audioGraphStore.releaseMicAnalyzer(micAnalyzerId);
      throw new Error('microphone source unavailable');
    }

    const format = getFormat();
    if (get().recordingState !== 'READY') throw Error('recorder not ready');

    const { id: streamListenerId, stream } = getRecorderStream(() => {
      dictationLogger.error({ event_type: 'INTERRUPTION' });
      setErrorCode('ERROR', 'FAILED_TO_GET_AUDIO_STREAM');
    });

    const chunkRecorder = new ChunkRecorder({
      chunkSpec: { chunkDurations: dictationChunkDurations },
      format,
      codec: getCodec(format),
      stream,
      micAnalyzer,
      micAnalyzerId,
      onChunkAvailable: async (chunk) => {
        const dictationChunk = { dictationId, dictationRecordingId, ...chunk };
        set((s) => ({
          numChunks: s.numChunks + 1,
          totalDuration: s.totalDuration + chunk.duration,
        }));

        // Hold the last chunk for enriched PP dispatch instead of sending it
        // immediately. The dictation slice's stop() will send it with
        // accumulated text + user context once all prior chunks have finished.
        if (get().pendingLastChunkEnabled) {
          set({
            pendingLastChunk: { chunk: dictationChunk, format, useLogsBackend },
            pendingLastChunkEnabled: false,
          });
          return;
        }

        set((s) => ({ numChunksProcessing: s.numChunksProcessing + 1 }));

        let text = '';

        try {
          const result = await pRetry(
            () =>
              transcribeChunk(
                dictationChunk,
                format,
                readDefaultDictationInputLanguages(),
                useLogsBackend
              ),
            {
              retries: 3,
              minTimeout: 500,
              maxTimeout: 1000,
              shouldRetry: (error) =>
                !(error instanceof AxiosError && error.response?.status === 401),
            }
          );
          text = result.text;
        } catch (err) {
          // PHI-safe diagnostics only (codes/status/online flag) — never the
          // raw error, which can carry request/response bodies. Correlation IDs
          // are auto-merged by dictationLogger.
          const failure = describeTranscriptionFailure(err);
          dictationLogger.error({
            event_type: 'DICTATION_CHUNK_FAILED',
            details: failure,
          });
          const { setErrorCode } = get();

          if (err instanceof AxiosError) {
            set((s) => ({ numChunksProcessing: s.numChunksProcessing - 1 }));
            // useMaxDuration already auto-stops and transcribes at the client
            // cap MAX_DICTATION_DURATION_MS. The BE enforces a longer
            // hard cap (413 + `DictationMaxDurationReached` code) as a backstop.
            if (
              err.response?.status === 413 &&
              getProp(err.response?.data, 'code') === 'DictationMaxDurationReached'
            ) {
              get().cancel();
              return;
            }

            setErrorCode(
              'ERROR',
              failure.failure_class === 'authentication'
                ? 'AUTHENTICATION_REQUIRED'
                : 'COULD_NOT_CONNECT_TO_HEIDI',
              false,
              { event_type: 'DICTATION_CHUNK_FAILED', details: { ...failure } }
            );
            get().stopRecording();
            return;
          }

          const checkConnectivity =
            useSessionRecordingStore.getState().config?.checkConnectivity;
          if (checkConnectivity) {
            try {
              await checkConnectivity();
            } catch (connectivityError) {
              dictationLogger.warn({
                event_type: 'DICTATION_CONNECTIVITY_CHECK_FAILED',
                details: {
                  errorName:
                    connectivityError instanceof Error
                      ? connectivityError.name
                      : typeof connectivityError,
                },
              });
              set((s) => ({ numChunksProcessing: s.numChunksProcessing - 1 }));
              setErrorCode('ERROR', 'COULD_NOT_CONNECT_TO_HEIDI');
              get().stopRecording();
              return;
            }
          }

          // Decrement for unhandled errors too and exit early
          set((s) => ({ numChunksProcessing: s.numChunksProcessing - 1 }));
          return;
        }

        set((s) => ({
          transcribedChunks: { ...s.transcribedChunks, [chunk.index]: text },
          numChunksProcessing: s.numChunksProcessing - 1,
          hasReceivedTranscript:
            s.hasReceivedTranscript || (text?.trim()?.length ?? 0) > 0,
        }));
      },
      onError: (errorReason: string) => {
        dictationLogger.error({
          event_type: 'CHUNK_RECORDER_FATAL_ERROR',
          details: { error_reason: errorReason },
        });
        get().setErrorCode('ERROR', 'CHUNK_RECORDER_ERROR');
      },
    });

    set({
      recordingState: 'ACTIVE',
      chunkRecorder,
      recorderStreamId: streamListenerId,
      numChunks: 0,
      // Reset transcription state from previous session
      numChunksProcessing: 0,
      totalDuration: 0,
      transcribedChunks: {},
      hasReceivedTranscript: false,
      pendingLastChunkEnabled: false,
      pendingLastChunk: null,
    });
  },

  stopRecording: () => {
    // Arm the intercept flag BEFORE stopping the recorder so the final
    // ondataavailable burst is held in pendingLastChunk rather than being
    // sent immediately.
    set({ pendingLastChunkEnabled: true });
    void get().chunkRecorder?.stop();
    const streamId = get().recorderStreamId;
    if (streamId) releaseStream(streamId);
    // Reset recordingState to allow new recordings, but keep chunkRecorder
    // alive so hasChunksInLimbo() can still be checked during processing
    set({ recordingState: 'READY', recorderStreamId: null });
  },

  resetRecording: () =>
    set((state) => ({
      ...initialState,
      recorderStreamId: state.recorderStreamId,
    })),
});
